import {
  ChangeDetectorRef,
  Component,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ViewChildren,
  QueryList,
  inject,
} from '@angular/core';
import { cloneDeep } from 'lodash';
import {
  ModalRef,
  BackendResponse,
  ReactiveComponent,
  deepClone,
  WorkflowItem,
  FileUploaderV2,
  FileV2Api,
  ModalModule,
  WorkflowModule,
  NotificationsV2Module,
  ButtonV2Module,
} from '@schenck/rotec-components';
import { BehaviorSubject, Observable, Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ToolingTaskInfo } from 'src/api';
import { ToolingTaskService, ToolingSearchService } from 'src/services';
import { SchedulingModel } from 'src/components/scheduling/scheduling.component';
import { TOOLING_WORKFLOW_ITEMS } from '../../../utils/constants/upsert-task.const';
import { StepComponent } from './steps/tooling-step-base.component';
import { DurationUnit } from 'src/utils/constants/duration.const';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { ToolingStepComponent } from './steps/tooling-step/tooling-step.component';
import { ToolingDescriptionStepComponent } from './steps/description-step/description-step.component';
import { ToolingSchedulingStepComponent } from './steps/scheduling-step/scheduling-step.component';
import { ToolingDocumentsStepComponent } from './steps/documents-step/documents-step.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface ToolingTaskUpsertModel extends ToolingTaskInfo {
  __scheduling?: SchedulingModel;
  customComponent?: string;
  /**
   * The Duration Unit which is selected (UI only)
   * This Unit must be converted back to Minutes in the request
   */
  expectedDurationUnit?: DurationUnit;
}

@Component({
  selector: 'ma-upsert-tooling-task-wizard',
  templateUrl: './upsert-tooling-task-wizard.component.html',
  styleUrls: ['./upsert-tooling-task-wizard.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToolingSearchService],
  imports: [
    ModalModule,
    WorkflowModule,
    NotificationsV2Module,
    ToolingStepComponent,
    ToolingDescriptionStepComponent,
    ToolingSchedulingStepComponent,
    ToolingDocumentsStepComponent,
    ButtonV2Module,
    NgTemplateOutlet,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class UpsertToolingTaskWizardComponent
  extends ReactiveComponent
  implements AfterViewInit, OnDestroy
{
  private readonly modalRef =
    inject<ModalRef<ToolingTaskInfo, (ToolingTaskInfo | null)[]>>(ModalRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly taskService = inject(ToolingTaskService);
  private readonly fileApi = inject(FileV2Api);
  public toolingSearchService = inject(ToolingSearchService);

  @ViewChildren('steps') public steps: QueryList<StepComponent>;

  public toolingTaskModel: ToolingTaskUpsertModel = <any>{};
  public workflowItems = deepClone([...TOOLING_WORKFLOW_ITEMS]);
  public activeStep: WorkflowItem = this.workflowItems[0];
  public nextClick$ = new Subject<void>();
  public previousClick$ = new Subject<void>();
  public loading$ = new BehaviorSubject<boolean>(false);
  public descriptionStepIndex = 1;
  public backendError = '';

  private fileUploader = new FileUploaderV2(this.fileApi);
  private stepFormChangesSubscription: Subscription;
  private _stepState: { first: boolean; last: boolean } = { first: false, last: false };

  public get edit(): boolean {
    return !!this.modalRef.data;
  }

  public get activeItemIndex(): number {
    return this.workflowItems.findIndex(({ title }) => this.activeStep.title === title);
  }

  public get createButton(): boolean {
    return this._stepState.last && this.activeItemIndex === this.workflowItems.length - 1;
  }

  public get nextButtonDisabled(): boolean {
    return !this.isCurrentStepValid;
  }

  public get previousButtonDisabled(): boolean {
    return this.activeItemIndex === 0;
  }

  public get isCurrentStepValid(): boolean {
    return this.steps?.first?.valid;
  }

  public get leftSaveButtonActive(): boolean {
    return this.edit && !this._stepState.last && this.workflowItems.every(({ valid }) => valid);
  }

  constructor() {
    super();
    if (this.edit) {
      this.toolingTaskModel = this.transformToUpsertModel(cloneDeep(this.modalRef.data));
      this.toolingSearchService.setSelected([this.modalRef.data.toolingId]);
      this.workflowItems = this.workflowItems.map((item, i) => ({
        ...item,
        valid: true,
        status: i === this.descriptionStepIndex ? 'active' : 'visited',
      }));
      this.activeStep = this.workflowItems[this.descriptionStepIndex];
    }
  }

  ngAfterViewInit() {
    this.steps.changes.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      // Unsubscribes from previous step form status changes
      this.stepFormChangesSubscription?.unsubscribe();
      this.updateWorkflowItemValidity();
      this.subscribeToStepFormStatusChanges();
      this.cdr.detectChanges();
    });
    this.subscribeToStepFormStatusChanges();
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.fileUploader.cancelFiles$().subscribe();
    this.nextClick$.complete();
    this.previousClick$.complete();
    this.loading$.complete();
    this.stepFormChangesSubscription?.unsubscribe();
  }

  public stepChanged(step: WorkflowItem): void {
    this.activeStep = step;
    this.setStepState();
    this.updateWorkflowItemValidity();
  }

  public setStepState(): void {
    this._stepState.last = this.activeItemIndex === this.workflowItems.length - 1;
    this._stepState.first = this.activeItemIndex === 0;
  }

  public close(): void {
    this.modalRef.close();
  }

  public submitToolingTask(): void {
    this.backendError = '';
    this.loading$.next(true);
    let toolingIds = this.toolingSearchService.state.selectedIds;

    const upsertAllTasks$ = toolingIds.map((toolingId) => {
      return this.upsertTask$(toolingId).pipe(
        filter((result) => BackendResponse.isSuccess(result)),
        tap(() => (toolingIds = toolingIds.filter((id) => id !== toolingId)))
      );
    });

    forkJoin(upsertAllTasks$).subscribe((results) => {
      this.loading$.next(false);
      const firstError = results.find((res) => !BackendResponse.isSuccess(res)) as any;

      if (firstError) {
        this.backendError = BackendResponse.isBadRequest(firstError)
          ? firstError.msg
          : 'TECHNICAL_ERROR_try again later or contact admin';
        // Remove from selected tooling those for which it worked
        this.toolingSearchService.setSelected(toolingIds);
      } else {
        this.modalRef.emitAndClose(
          (<BackendResponse.Success<ToolingTaskInfo | null>[]>results).map((res) => res.result)
        );
      }
    });
  }

  private updateWorkflowItemValidity(): void {
    this.workflowItems[this.activeItemIndex].valid = this.isCurrentStepValid;
  }

  private subscribeToStepFormStatusChanges(): void {
    this.stepFormChangesSubscription = this.steps.first.stepForm.statusChanges.subscribe(() =>
      this.updateWorkflowItemValidity()
    );
  }

  private upsertTask$(toolingId: string): Observable<any> {
    const model: ToolingTaskInfo = {
      ...this.transformBackIntoRealModel(),
      toolingId,
    };
    const upsert$ = model.id ? this.taskService.update$(model) : this.taskService.create$(model);
    return this.fileUploader.upsertFiles$().pipe(
      switchMap(() => upsert$),
      catchError(() => of(BackendResponse.backendError(null)))
    );
  }

  private transformBackIntoRealModel(): ToolingTaskInfo {
    let model: ToolingTaskUpsertModel = {
      ...this.toolingTaskModel,
    };
    // merge scheduling
    if (model.__scheduling.single === true) {
      model.dueDate = model.__scheduling.dueDate;
    } else {
      model = { ...model, ...model.__scheduling };
    }
    delete model.__scheduling;

    return model;
  }

  private transformToUpsertModel(model: ToolingTaskInfo): ToolingTaskUpsertModel {
    const upsertModel: ToolingTaskUpsertModel = {
      ...model,
    };
    // Add scheduling
    upsertModel.__scheduling = !!model.scheduling
      ? { single: false, scheduling: model.scheduling }
      : { single: true, dueDate: model.dueDate };

    return upsertModel;
  }
}
